import { memo, useCallback } from 'react';
import type { RefObject, SyntheticEvent } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import type { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import HilSegmentsRetryAllToolbarButton from 'app/pages/HumanInTheLoopPage/components/HilSegmentsRetryAllToolbarButton';
import type { HilErrorCategory } from 'app/pages/HumanInTheLoopPage/types';
import type { HilSegmentAttentionActionsBridge } from 'app/pages/HumanInTheLoopPage/utils/hilSegmentAttentionActionsBridge';

type HilSegmentsAttentionGridsIslandProps = {
  actionsBridgeRef: RefObject<HilSegmentAttentionActionsBridge>;
  dataGridRef: RefObject<DataGridMAIQRef>;
  functionalGridType: NonNullable<unknown>;
  systemGridType: NonNullable<unknown>;
  refreshKey: number;
  segmentsErrorCategory: HilErrorCategory;
  onSegmentsErrorCategoryChange: (category: HilErrorCategory) => void;
  onFunctionalTotalDocuments: (totalDocuments: number) => void;
  onSystemTotalDocuments: (totalDocuments: number) => void;
  retryAllLoading: boolean;
  resolveActionLoading: boolean;
};

const FUNCTIONAL_GRID_KEY = 'hil-segments-functional';
const SYSTEM_GRID_KEY = 'hil-segments-system';

function HilSegmentsAttentionGridsIsland({
  actionsBridgeRef,
  dataGridRef,
  functionalGridType,
  systemGridType,
  refreshKey,
  segmentsErrorCategory,
  onSegmentsErrorCategoryChange,
  onFunctionalTotalDocuments,
  onSystemTotalDocuments,
  retryAllLoading,
  resolveActionLoading,
}: HilSegmentsAttentionGridsIslandProps) {
  const activeTabIndex = segmentsErrorCategory === 'system' ? 1 : 0;

  const handleGridCellClick = useCallback(
    (cell: unknown, clickedElement?: string) => {
      actionsBridgeRef.current?.handleCellClick(
        cell as { row?: unknown },
        clickedElement,
      );
    },
    [actionsBridgeRef],
  );

  const handleRetryAllClick = useCallback(() => {
    actionsBridgeRef.current?.openRetryAllConfirm();
  }, [actionsBridgeRef]);

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, index: number) => {
      onSegmentsErrorCategoryChange(index === 1 ? 'system' : 'functional');
    },
    [onSegmentsErrorCategoryChange],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={activeTabIndex}
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            fontWeight: 500,
            textTransform: 'none',
          },
          '& .MuiTab-root.Mui-selected': {
            fontWeight: 700,
          },
        }}
      >
        <Tab label="Functional" />
        <Tab label="System" />
      </Tabs>

      <Box sx={{ mt: '-1px', overflow: 'visible' }}>
        {activeTabIndex === 0 ? (
          <DataGridMAIQ
            key={FUNCTIONAL_GRID_KEY}
            ref={dataGridRef}
            type={functionalGridType}
            refreshKey={refreshKey}
            onTotalDocumentsChange={onFunctionalTotalDocuments}
            onCellClick={handleGridCellClick}
            toolbarRightSlot={
              <HilSegmentsRetryAllToolbarButton
                onClick={handleRetryAllClick}
                loading={retryAllLoading}
                disabled={resolveActionLoading}
              />
            }
          />
        ) : (
          <DataGridMAIQ
            key={SYSTEM_GRID_KEY}
            type={systemGridType}
            refreshKey={refreshKey}
            onTotalDocumentsChange={onSystemTotalDocuments}
          />
        )}
      </Box>
    </Box>
  );
}

function arePropsEqual(
  prev: HilSegmentsAttentionGridsIslandProps,
  next: HilSegmentsAttentionGridsIslandProps,
): boolean {
  return (
    prev.actionsBridgeRef === next.actionsBridgeRef &&
    prev.dataGridRef === next.dataGridRef &&
    prev.functionalGridType === next.functionalGridType &&
    prev.systemGridType === next.systemGridType &&
    prev.refreshKey === next.refreshKey &&
    prev.segmentsErrorCategory === next.segmentsErrorCategory &&
    prev.onFunctionalTotalDocuments === next.onFunctionalTotalDocuments &&
    prev.onSystemTotalDocuments === next.onSystemTotalDocuments &&
    prev.onSegmentsErrorCategoryChange === next.onSegmentsErrorCategoryChange &&
    prev.retryAllLoading === next.retryAllLoading &&
    prev.resolveActionLoading === next.resolveActionLoading
  );
}

export default memo(HilSegmentsAttentionGridsIsland, arePropsEqual);
